import React, { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './HomeHowItWorks.css';
import { formatUSD } from '../../utils/money';
import { buyerTotal, sellerPayout, SELLER_FEE_PCT } from '../../utils/fees';
import { universityBadge } from '../Social/universityTheme';

// Scripted product scenes for the homepage "How it works" screen.
//
// Each scene is a fixed 600 x 400 mini-UI plus a list of timed cues. A cue is
// [name, atMs, meta]; the scene component reads `f(name)` ("has this cue
// fired yet?") to decide what to show, and `meta` can also drive the status
// pill ({ pill: [text, tone] }), move the cursor ({ cur: 'data-cur name' }) or
// click it ({ press: true }). All names, prices and files are illustrative,
// but the flow is the real one (2026-10-19): the order request sheet over the
// listing, then the order page. Nothing here goes through the inbox.

const Instant = createContext(false);
// Milliseconds into the current scene. Everything that moves reads this one
// clock (see SceneScreen), so pausing the section freezes typing and
// count-ups too, and nothing can drift out of step with the progress bar.
const Clock = createContext(0);

/* ─── tiny pieces ──────────────────────────────────────────────────────────── */

// Types `text` out from the `from` ms mark, `cps` characters a second.
function Typed({ text, from, cps = 40 }) {
  const instant = useContext(Instant);
  const t = useContext(Clock);
  if (instant) return text;
  if (from == null || t < from) return '';
  return text.slice(0, Math.floor(((t - from) * cps) / 1000));
}
Typed.propTypes = { text: PropTypes.string.isRequired, from: PropTypes.number, cps: PropTypes.number };

const h = (on, cls = '') => `hw-h${on ? ' hw-in' : ''}${cls ? ` ${cls}` : ''}`;

const Check = ({ size = 9 }) => (
  <svg width={size} height={size} viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 5.2l2 2 4-4.4" />
  </svg>
);
Check.propTypes = { size: PropTypes.number };

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7.5" />
    <path d="M21 21l-4.6-4.6" />
  </svg>
);
const LockIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4.5" y="11" width="15" height="10" rx="2" />
    <path d="M8 11V7.5a4 4 0 018 0V11" />
  </svg>
);
LockIcon.propTypes = { size: PropTypes.number };
const FileIcon = ({ size = 17 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" />
    <path d="M14 3v5h5" />
  </svg>
);
FileIcon.propTypes = { size: PropTypes.number };
const PenIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 20h4L19 9l-4-4L4 16z" />
    <path d="M13.5 6.5l4 4" />
  </svg>
);
const ClockIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 2" />
  </svg>
);
const ArrowIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

// The scenes use short school names; the full name picks the school's colours.
const SCHOOL_FULL = {
  OSU: 'Oregon State University',
  'Oregon State': 'Oregon State University',
  UO: 'University of Oregon',
  PSU: 'Portland State University',
};
const Verified = ({ school }) => {
  const b = universityBadge(SCHOOL_FULL[school] || school);
  return (
    <span className="hw-chip" style={{ background: b.tint, color: b.ink }}>
      <Check />
      {school} verified
    </span>
  );
};
Verified.propTypes = { school: PropTypes.string.isRequired };

const Av = ({ children, ink }) => <span className={`hw-av${ink ? ' ink' : ''}`}>{children}</span>;
Av.propTypes = { children: PropTypes.node.isRequired, ink: PropTypes.bool };

// Service covers are real photos (Unsplash license, self-hosted):
// unsplash.com/photos/photo-1572044162444-ad60f128bdea,
// photo-1675238814757-42c4c7e09ea8 (Sanket Mishra), photo-1749410342681-3510f9edb7ad (André Eusébio).
//
// The delivered files are authored artwork: the brand kit Theo's brief asked
// for ("a logo and brand kit for our robotics club, navy and gold, for the
// fall fair"), so one mark carries through the logo, the merch and the
// poster the way a real kit does.
const NAVY = '#16213D';
const GOLD = '#D4A72C';
const CREAM = '#F4EFE3';

// The club's mark: a twelve-tooth gear (a dashed stroke draws the teeth)
// with a robot face inside. `ring` is the gear, `face` the robot.
const RoboMark = ({ x, y, s = 1, ring, face }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`}>
    <circle r="24" fill="none" stroke={ring} strokeWidth="6" strokeDasharray="6.28 6.28" />
    <circle r="20" fill={ring} />
    <line x1="0" y1="-10" x2="0" y2="-15" stroke={face} strokeWidth="2.2" strokeLinecap="round" />
    <circle cy="-16" r="2.4" fill={face} />
    <rect x="-11.5" y="-9.5" width="23" height="18" rx="5.5" fill={face} />
    <circle cx="-5" cy="-1.5" r="2.7" fill={ring} />
    <circle cx="5" cy="-1.5" r="2.7" fill={ring} />
    <rect x="-4.5" y="3.6" width="9" height="1.8" rx=".9" fill={ring} />
  </g>
);
RoboMark.propTypes = { x: PropTypes.number, y: PropTypes.number, s: PropTypes.number, ring: PropTypes.string, face: PropTypes.string };

const ART_FONT = { fontFamily: 'Manrope, system-ui, sans-serif' };
const DELIVERED = [
  {
    label: 'Primary logo',
    art: (
      <svg viewBox="0 0 170 86" aria-hidden="true" style={ART_FONT}>
        <rect width="170" height="86" fill={NAVY} />
        <circle cx="38" cy="43" r="34" fill="#1E2C4F" />
        <RoboMark x={38} y={44} ring={GOLD} face={NAVY} />
        <text x="76" y="44" fill={CREAM} fontSize="16" fontWeight="800" letterSpacing="-.3">ROBOTICS</text>
        <text x="77" y="58" fill={GOLD} fontSize="8" fontWeight="700" letterSpacing="1.4">CLUB · EST 2019</text>
      </svg>
    ),
  },
  {
    label: 'Club tee',
    art: (
      <svg viewBox="0 0 170 86" aria-hidden="true" style={ART_FONT}>
        <rect width="170" height="86" fill="#E7E1D4" />
        <ellipse cx="85" cy="80" rx="34" ry="3.5" fill="#000" opacity=".09" />
        <path d="M63 17 L74 11 Q85 19 96 11 L107 17 L121 31 L111 39 L106 34 L106 77 L64 77 L64 34 L59 39 L49 31 Z" fill={NAVY} />
        <path d="M74 11 Q85 19 96 11" fill="none" stroke="#24335A" strokeWidth="2.4" />
        <RoboMark x={85} y={46} s={0.62} ring={GOLD} face={NAVY} />
      </svg>
    ),
  },
  {
    label: 'Fall fair poster',
    art: (
      <svg viewBox="0 0 170 86" aria-hidden="true" style={ART_FONT}>
        <rect width="170" height="86" fill={GOLD} />
        <circle cx="146" cy="16" r="40" fill="#C99A1F" />
        <RoboMark x={151} y={22} s={0.5} ring={NAVY} face={GOLD} />
        <text x="12" y="22" fill={NAVY} fontSize="7.5" fontWeight="800" letterSpacing="1.6">ROBOTICS CLUB</text>
        <text x="11" y="50" fill={NAVY} fontSize="23" fontWeight="800" letterSpacing="-1">FALL FAIR</text>
        <text x="12" y="68" fill={NAVY} fontSize="8" fontWeight="700" opacity=".78">Booth 12 · Oct 24 · Live demos</text>
      </svg>
    ),
  },
];

// One sample order runs through every scene: Theo buys Celest's Standard
// package. Money comes from the real fee code, so the figures stay true if
// the fees change.
const PRICE = 90;
const PRICE_TXT = formatUSD(PRICE, 2);
const PAY_TXT = formatUSD(buyerTotal(PRICE), 2);
const PAYOUT_TXT = formatUSD(sellerPayout(PRICE), 2);
const BRIEF = 'A logo and brand kit for our robotics club. Navy and gold, needed for the fall fair.';

/* ─── scenes ───────────────────────────────────────────────────────────────── */

// Celest's listing cover: her portfolio, logos she made for other student
// orgs, the way real service covers show sample work (2026-10-19, replacing
// a stock photo of a drawing tablet). Theo's own kit is what she delivers
// later, so it doesn't appear here. `slice` lets the search card crop it.
const PortfolioCover = ({ className }) => (
  <svg className={className} viewBox="0 0 366 200" preserveAspectRatio="xMidYMid slice" aria-hidden="true" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
    <rect width="366" height="200" fill="#EFE9DD" />
    <rect x="14" y="14" width="152" height="172" rx="10" fill="#1D2B53" />
    <circle cx="90" cy="82" r="38" fill="none" stroke="#E2B33C" strokeWidth="3" />
    <circle cx="90" cy="82" r="31" fill="none" stroke="#E2B33C" strokeWidth="1" opacity=".6" />
    <text x="90" y="95" fill="#F4EFE3" fontSize="34" fontWeight="800" textAnchor="middle" letterSpacing="-1">DS</text>
    <text x="90" y="146" fill="#F4EFE3" fontSize="11" fontWeight="800" textAnchor="middle" letterSpacing="2.4">DEBATE</text>
    <text x="90" y="161" fill="#E2B33C" fontSize="8" fontWeight="700" textAnchor="middle" letterSpacing="2.6">SOCIETY</text>
    <rect x="176" y="14" width="176" height="82" rx="10" fill="#E4572E" />
    <rect x="192" y="34" width="34" height="42" rx="4" fill="none" stroke="#FFF3E8" strokeWidth="3" />
    <path d="M192 44h34M192 66h34" stroke="#FFF3E8" strokeWidth="2" />
    <path d="M204 49l10 6-10 6z" fill="#FFF3E8" />
    <text x="238" y="54" fill="#FFF3E8" fontSize="20" fontWeight="800" letterSpacing="-.6">film</text>
    <text x="238" y="72" fill="#FFF3E8" fontSize="13" fontWeight="600" letterSpacing="-.2" opacity=".9">society</text>
    <rect x="176" y="104" width="84" height="82" rx="10" fill="#2F6B4F" />
    <path d="M190 160l20-32 12 18 8-11 18 25z" fill="#CFE8C4" />
    <circle cx="238" cy="124" r="6" fill="#F2D27A" />
    <text x="218" y="177" fill="#CFE8C4" fontSize="8" fontWeight="800" textAnchor="middle" letterSpacing="1.8">OUTDOORS</text>
    <rect x="268" y="104" width="84" height="82" rx="10" fill="#E2B33C" />
    <text x="310" y="155" fill="#1D2B53" fontSize="44" fontWeight="800" textAnchor="middle">Σ</text>
    <text x="310" y="177" fill="#1D2B53" fontSize="8" fontWeight="800" textAnchor="middle" letterSpacing="1.8">MATH CLUB</text>
  </svg>
);
PortfolioCover.propTypes = { className: PropTypes.string };

const SERVICES = [
  { av: 'CG', name: 'Celest G.', school: 'OSU', title: 'Logo and brand kit for student orgs', price: '$90', cover: true },
  { av: 'RT', name: 'Robert T.', school: 'PSU', title: 'Event photography for club nights', price: '$120', img: 'photography' },
  { av: 'FO', name: 'Fiona O.', school: 'UO', title: 'Recap videos for student events', price: '$70', img: 'video-editing' },
];

function SearchScene({ f, at }) {
  return (
    <>
      <div className="hw-ssearch">
        <SearchIcon />
        <span>
          <Typed text="help for our student org" from={at('q')} cps={40} />
        </span>
        <i className="hw-caret" />
      </div>
      <p className={h(f('c1'), 'hw-scap')}>Services from verified students and alumni</p>
      <div className="hw-sgrid">
        {SERVICES.map((s, i) => (
          <div
            key={s.name}
            className={h(f(`c${i + 1}`), `hw-svc hw-card${i === 0 && f('clk') ? ' is-hit' : ''}`)}
            data-cur={i === 0 ? 'card0' : undefined}
            data-cy=".6"
          >
            {s.cover
              ? <PortfolioCover className="hw-cv" />
              : <img className="hw-cv" src={`/images/how-it-works/${s.img}.jpg`} alt="" width="720" height="312" />}
            <div className="hw-bd">
              <div className="hw-sl">
                <Av>{s.av}</Av>
                {s.name} <Verified school={s.school} />
              </div>
              <h4>{s.title}</h4>
              <div className="hw-ft">
                <b>{s.price}</b>
                <span className="hw-chip grey">Fixed</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// The order request sheet (components/Agreements/OrderRequestSheet.jsx): it
// slides over the listing, asks for the brief, the package, then a
// signature, and charges nothing. Same labels as the real one.
const SHEET_STEPS = ['Brief', 'Package', 'Sign'];
const SHEET_TIERS = [['Basic', '$40', '3 days · 1 revision'], ['Standard', '$90', '5 days · 2 revisions'], ['Premium', '$160', '7 days · 3 revisions']];
function RequestScene({ f, at }) {
  const page = f('sign') ? 2 : f('pkg') ? 1 : 0;
  const sent = f('sent');
  return (
    <>
      <div className="hw-lst">
        <PortfolioCover className="hw-lst-cv" />
        <div className="hw-lst-b">
          <div className="hw-sl">
            <Av>CG</Av>
            Celest G.
          </div>
          <b>Logo and brand kit for student orgs</b>
        </div>
        <div className="hw-lst-pk hw-card">
          <span className="hw-sm">Standard</span>
          <strong>{PRICE_TXT}</strong>
          <span className="hw-sm">5 days · 2 revisions</span>
          <span className={`hw-btn${f('clk1') ? ' hw-hit' : ''}`} data-cur="rq">Request to order</span>
        </div>
      </div>
      <div className={`hw-scrim${f('open') ? ' is-on' : ''}`} />
      <div className={`hw-sheet${f('open') ? ' is-on' : ''}`}>
        <div className="hw-sh-h">
          <b>Request from Celest G.</b>
          <span className="hw-sm">Standard · nothing is charged until Celest signs</span>
          <ol className="hw-sh-steps">
            {SHEET_STEPS.map((s, i) => <li key={s} className={i <= page ? 'on' : ''}><i />{s}</li>)}
            <li className="later"><i />Pay later</li>
          </ol>
        </div>
        <div className="hw-sh-b">
          {page === 0 && (
            <>
              <p className="hw-sh-q">What do you need from Celest?</p>
              <div className="hw-sh-ta">
                <Typed text={BRIEF} from={at('brief')} cps={45} />
                {f('brief') && !f('pkg') && <i className="hw-caret" />}
              </div>
            </>
          )}
          {page === 1 && (
            <>
              <p className="hw-sh-q">Pick your package</p>
              {SHEET_TIERS.map(([n, p, d], i) => (
                <div key={n} className={`hw-sh-tier${i === 1 ? ' is-sel' : ''}`}>
                  <i />
                  <span><b>{n}</b><span className="hw-sm">{d}</span></span>
                  <b>{p}</b>
                </div>
              ))}
            </>
          )}
          {page === 2 && (
            <>
              <p className="hw-sh-q">Sign and send your request</p>
              <dl className="hw-sh-recap">
                <div><dt>Your brief</dt><dd>{BRIEF}</dd></div>
                <div><dt>Package</dt><dd>Standard as listed: {PRICE_TXT}, 5 days</dd></div>
              </dl>
              <span className="hw-sh-lbl">Sign with your full legal name</span>
              <div className={`hw-sig${f('sig') ? ' is-signed' : ''}`}><span>Theo Bramwell</span></div>
              <span className="hw-sm">Signing doesn&rsquo;t charge you</span>
            </>
          )}
        </div>
        <div className="hw-sh-f">
          <span><strong>$0.00</strong><span className="hw-sm">due today</span></span>
          <span className={`hw-btn${f('clk2') && !f('pkg') ? ' hw-hit' : ''}${f('clk3') && !f('sign') ? ' hw-hit' : ''}${f('clk4') ? ' hw-hit' : ''}`} data-cur="nx" data-cx=".6">
            {sent ? <>Request sent <Check size={12} /></> : page === 0 ? 'Next: the package' : page === 1 ? 'Next: sign' : 'Sign and send'}
            {!sent && <ArrowIcon />}
          </span>
        </div>
      </div>
    </>
  );
}

// The order page (pages/Orders/OrderDetail.jsx, "Clean data"): header card,
// the next-step callout (black when it's the viewer's move), then order
// progress beside the Payment box. Row labels are stagesOf()'s own.
const AMBER = 'amber';
// `contract` gives the contract page's header (a hire from a job post).
function OrderHead({ status, tone, seller, contract }) {
  return (
    <div className="hw-oh hw-card">
      <div className="hw-oh-t">
        <b>{contract ? JOB.title : 'Logo and brand kit for student orgs'}</b>
        <span className={`hw-ost ${tone || ''}`}>{status}</span>
      </div>
      <div className="hw-oh-f">
        <span>{seller ? 'Client' : 'Freelancer'} <b>{seller ? 'Theo B.' : contract ? JOB.freelancer : 'Celest G.'}</b></span>
        {contract ? <span>Budget <b>Fixed price</b></span> : <span>Package <b>Standard</b></span>}
        <span>Ref <b>{contract ? '#4F2A9C' : 'UF-4821'}</b></span>
        <strong>{contract ? JOB_PRICE_TXT : PRICE_TXT}</strong>
      </div>
    </div>
  );
}
OrderHead.propTypes = { status: PropTypes.string.isRequired, tone: PropTypes.string, seller: PropTypes.bool, contract: PropTypes.bool };

// `done` drops the Your move / Waiting prefix: nobody owes anything.
function Callout({ mine, done, icon, lead, rest, children }) {
  return (
    <div className={`hw-ocall${mine ? ' is-mine' : ''}`}>
      <p>
        <span className="hw-oic">{icon}</span>
        <span>{done ? <b>{lead}</b> : <><b>{mine ? 'Your move: ' : 'Waiting: '}</b>{lead}</>}{rest && <>.<span className="hw-orest">{rest}</span></>}</span>
      </p>
      {children && <div className="hw-oacts">{children}</div>}
    </div>
  );
}
Callout.propTypes = { mine: PropTypes.bool, done: PropTypes.bool, icon: PropTypes.node, lead: PropTypes.node, rest: PropTypes.node, children: PropTypes.node };

function Progress({ rows, title = 'Order progress' }) {
  const done = rows.filter((r) => r.done).length;
  return (
    <div className="hw-oprog hw-card">
      <div className="hw-oprog-h"><b>{title}</b><span className="hw-sm">{done} of {rows.length} complete</span></div>
      <ol>
        {rows.map((r) => (
          <li key={r.k} className={r.done ? 'done' : r.mine ? 'mine' : ''}>
            <i>{r.done && <Check size={9} />}</i>
            {r.label}
          </li>
        ))}
      </ol>
    </div>
  );
}
Progress.propTypes = { rows: PropTypes.arrayOf(PropTypes.object).isRequired, title: PropTypes.string };

function PayBox({ held, seller, amount }) {
  return (
    <div className="hw-opay hw-card">
      <b>Payment</b>
      <strong className={held ? 'is-held' : ''}>{amount || (seller ? PAYOUT_TXT : PAY_TXT)}</strong>
      <span className="hw-sm">{seller ? `Your share after the ${SELLER_FEE_PCT}% fee` : 'Price, service fee and card processing'}</span>
      <span className={`hw-oheld${held ? ' is-on' : ''}`}>
        <LockIcon size={13} />
        {held ? 'Held in escrow' : 'Not paid yet'}
      </span>
    </div>
  );
}
PayBox.propTypes = { held: PropTypes.bool, seller: PropTypes.bool, amount: PropTypes.string };

// Buyer: Celest countersigns, payment opens, Theo pays into escrow.
// Celest's signature is shown being written, then held signed, before the
// pay box takes over: the countersign is the step's whole point and was
// gone too fast to follow (2026-10-19).
function OrderPayScene({ f }) {
  const signing = f('cs');
  const signed = f('sg');
  const open = f('open');
  const paid = f('paid');
  const rows = [
    { k: 'sent', label: 'Service agreement sent', done: true },
    { k: 'sign', label: signed ? 'Service agreement signed' : 'Celest reviews and signs', done: signed },
    { k: 'pay', label: paid ? 'Payment held in escrow' : 'Pay into escrow', done: paid, mine: open },
    { k: 'dl', label: 'Celest delivers the work', done: false },
    { k: 'ok', label: 'Approve · release payment', done: false },
  ];
  return (
    <div className="hw-ord">
      <OrderHead
        status={paid ? 'In progress' : signed ? 'Awaiting payment' : 'Awaiting agreement'}
        tone={paid ? '' : AMBER}
      />
      {!signing && <Callout icon={<PenIcon />} lead="Celest reviews and signs" rest=" Their signature opens payment." />}
      {signing && !open && (
        <Callout
          icon={<PenIcon />}
          lead={signed ? 'Celest signed the service agreement' : 'Celest is signing'}
          rest={signed ? ' Payment is open.' : ' Your brief and terms, as you sent them.'}
        >
          {/* Mounted unsigned, signed a beat later, so the write-in runs. */}
          <div className={`hw-sig hw-sig-ink${f('csw') ? ' is-signed' : ''}`}><span>Celest Grant</span></div>
        </Callout>
      )}
      {open && !paid && (
        <Callout mine icon={<LockIcon size={15} />} lead={`Pay ${PAY_TXT} into escrow`} rest=" Released to Celest only when you approve.">
          <span className={`hw-btn hw-onink${f('clk') ? ' hw-hit' : ''}`} data-cur="pay">Pay now</span>
        </Callout>
      )}
      {paid && <Callout icon={<ClockIcon />} lead="Celest delivers the work" rest=" Due in 5 days." />}
      <div className="hw-ogrid">
        <Progress rows={rows} />
        <PayBox held={paid} />
      </div>
    </div>
  );
}

// Seller: Theo's signed request arrives, Celest countersigns, Theo pays.
function SellerOrderScene({ f }) {
  const signed = f('signed');
  const paid = f('paid');
  const rows = [
    { k: 'got', label: 'Service agreement received', done: true },
    { k: 'sign', label: signed ? 'Service agreement signed' : 'Review and sign', done: signed, mine: !signed },
    { k: 'pay', label: paid ? 'Payment held in escrow' : 'Theo pays into escrow', done: paid },
    { k: 'dl', label: 'Deliver the work', done: false, mine: paid },
    { k: 'ok', label: 'Theo approves · you’re paid', done: false },
  ];
  return (
    <div className="hw-ord">
      <OrderHead seller status={paid ? 'In progress' : signed ? 'Awaiting payment' : 'Awaiting agreement'} tone={paid ? '' : AMBER} />
      {!signed && (
        <Callout mine icon={<PenIcon />} lead="Review Theo’s request and sign" rest={<> &ldquo;{BRIEF.split('.')[0]}.&rdquo;</>}>
          {/* Mounted unsigned on the click, signed a beat later, so the
              signature's clip-path transition actually runs. */}
          {f('clk')
            ? <div className={`hw-sig hw-sig-sm${f('sgn') ? ' is-signed' : ''}`}><span>Celest Grant</span></div>
            : <span className={`hw-btn hw-onink${f('clk') ? ' hw-hit' : ''}`} data-cur="sign">Review and sign</span>}
        </Callout>
      )}
      {signed && !paid && <Callout icon={<LockIcon size={15} />} lead="Theo pays into escrow" rest=" You start once it’s held." />}
      {paid && <Callout mine icon={<ClockIcon />} lead="Deliver the work" rest=" Due in 5 days." />}
      <div className="hw-ogrid">
        <Progress rows={rows} />
        <PayBox seller held={paid} />
      </div>
    </div>
  );
}

// Buyer: the delivery on the order page, with the approve button in the callout.
function DeliveryCard({ f, files = DELIVERED, zip = 'robotics-club-brand-kit.zip' }) {
  return (
    <div className="hw-odl hw-card">
        <div className="hw-oprog-h">
          <b>Delivery</b>
          <span className={h(f('fm'), 'hw-dlz')}><FileIcon size={13} />{zip} · Download</span>
        </div>
        <div className="hw-files">
          {files.map((d, i) => (
            <div key={d.label} className={h(f(`m${i + 1}`), 'hw-fl')}>
              {d.art}
              <span className="hw-sm">{d.label}</span>
            </div>
          ))}
        </div>
    </div>
  );
}
DeliveryCard.propTypes = { f: PropTypes.func.isRequired, files: PropTypes.array, zip: PropTypes.string };

function DeliverScene({ f }) {
  return (
    <div className="hw-ord">
      <OrderHead status="Delivered" tone={AMBER} />
      <DeliveryCard f={f} />
      <div className={h(f('acts'))}>
        <Callout mine icon={<Check size={14} />} lead="Review Celest’s delivery" rest=" Auto-releases in 7 days.">
          <span className="hw-btn hw-ghost-ink">Request a revision · 2 left</span>
          <span className={`hw-btn hw-onink${f('clk') ? ' hw-hit' : ''}`} data-cur="appr">Approve &amp; release</span>
        </Callout>
      </div>
    </div>
  );
}

function ReleaseScene({ f }) {
  return (
    <div className={`hw-rel${f('show') ? ' hw-in' : ''}`}>
      <svg className="hw-ck" viewBox="0 0 74 74" fill="none" stroke="#1F7A4D" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle className="c" cx="37" cy="37" r="30" />
        <path className="t" d="M25 38l9 9 16-18" />
      </svg>
      <h3 className={h(f('h'))}>{PRICE_TXT} released to Celest G.</h3>
      <p className={h(f('p'))}>
        Order complete. Thanks for hiring a verified student.
      </p>
      <div className={h(f('stars'), 'hw-stars')}>
        {[1, 2, 3, 4, 5].map((n) => (
          <svg key={n} viewBox="0 0 24 24" strokeLinejoin="round" className={f(`s${n}`) ? 'on' : ''} aria-hidden="true">
            <path d="M12 3.2l2.6 5.5 6 .8-4.4 4.2 1.1 6-5.3-2.9-5.3 2.9 1.1-6L3.4 9.5l6-.8z" />
          </svg>
        ))}
      </div>
      <p className={h(f('rv'), 'hw-sm hw-rev')}>
        Leave a review to help the next buyer
      </p>
    </div>
  );
}

/* ─── Start selling: the listing, the dashboard, the payout ─────────────────── */
// Celest's side of the same order Theo places in the hire track.

// Counts up from `from` to `to` starting at the `at` ms mark, on the scene
// clock, so pausing freezes it too.
function CountUp({ from = 0, to, at, ms = 1100 }) {
  const instant = useContext(Instant);
  const t = useContext(Clock);
  const p = instant ? 1 : at == null || t < at ? 0 : Math.min(1, (t - at) / ms);
  return formatUSD(from + (to - from) * (1 - (1 - p) ** 3), 2);
}
CountUp.propTypes = { from: PropTypes.number, to: PropTypes.number.isRequired, at: PropTypes.number, ms: PropTypes.number };

// The listing editor beside its Live Preview (pages/FindWork/CreateListing.jsx
// and ListingLivePreview.jsx): the preview builds itself from what's typed.
const LISTING_TITLE = 'Logo and brand kit for student orgs';
const PACKAGES = [['Basic', '$40', '3 days'], ['Standard', '$90', '5 days'], ['Premium', '$160', '7 days']];
function ListingScene({ f, at }) {
  const live = f('live');
  return (
    <>
      <div className="hw-ls">
        <div className="hw-ls-ed">
          <span className="hw-sh-lbl">Title</span>
          <div className="hw-fbox">
            <Typed text={LISTING_TITLE} from={at('t')} cps={45} />
            {f('t') && !f('p1') && <i className="hw-caret" />}
          </div>
          <span className="hw-sh-lbl">Packages, at your prices</span>
          {PACKAGES.map(([name, price, days], i) => (
            <div key={name} className={h(f(`k${i + 1}`), 'hw-ls-pk')}>
              <b>{name}</b>
              <span className="hw-sm">{days}</span>
              <strong><Typed text={price} from={at(`p${i + 1}`)} cps={12} /></strong>
            </div>
          ))}
          <span className="hw-sh-lbl">Gallery</span>
          <div className="hw-ls-gal">
            <div className={h(f('g'), 'hw-ls-th')}><PortfolioCover className="hw-ls-thsvg" /></div>
            <div className="hw-ls-add">+</div>
          </div>
        </div>
        <div className="hw-ls-pv">
          <span className="hw-ls-pvh">Live preview{live && <span className="hw-chip ok"><Check />Live</span>}</span>
          <div className="hw-svc hw-card hw-ls-card">
            {f('g') ? <PortfolioCover className="hw-cv" /> : <div className="hw-cv hw-ls-cvph" />}
            <div className="hw-bd">
              <div className="hw-sl"><Av>CG</Av>Celest G. <Verified school="OSU" /></div>
              <h4><Typed text={LISTING_TITLE} from={at('t')} cps={45} /></h4>
              <div className="hw-ft">
                <b>{f('p1') ? 'From $40' : ' '}</b>
                <span className="hw-chip grey">Fixed</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="hw-foot">
        <span>Listing is free. It goes live when you publish.</span>
        <span className={`hw-btn hw-brass${f('clk') ? ' hw-hit' : ''}`} data-cur="pub">{live ? <>Published <Check size={12} /></> : 'Publish service'}</span>
      </div>
    </>
  );
}

// The Selling dashboard's Overview (pages/SellerDashboard/SellerDashboard.jsx,
// components/Dashboard/SellerDesk.jsx): the ink rail, the stat tiles and the
// Active orders table, where Theo's signed request arrives as your move.
const DESK_ROWS = [
  { av: 'JL', who: 'Jamie L.', work: 'Club flyer set', total: '$60.00', status: 'In progress', tone: '' },
  { av: 'AP', who: 'Ana P.', work: 'Event logo', total: '$75.00', status: 'Waiting on Ana', tone: 'amber' },
];
function RequestsScene({ f }) {
  const arrived = f('new');
  const rows = arrived
    ? [{ av: 'TB', who: 'Theo B.', work: 'Logo and brand kit', total: '$90.00', status: 'Review and sign', tone: 'mine', fresh: true }, ...DESK_ROWS]
    : DESK_ROWS;
  return (
    <div className="hw-desk">
      <div className="hw-desk-rail">
        <b>Welcome back, Celest</b>
        <span className="hw-desk-sub">Selling</span>
        <ul>
          <li className="on">Overview</li><li>My services</li><li>Work{arrived && <i>1</i>}</li><li>Payouts</li>
        </ul>
      </div>
      <div className="hw-desk-main">
        <b className="hw-desk-t">Overview</b>
        <div className="hw-desk-tiles">
          <div><span>Earned in October</span><strong>$340.00</strong></div>
          <div className={arrived ? 'is-hot' : ''}><span>Active orders</span><strong>{arrived ? 3 : 2}</strong>{arrived && <em>1 needs you</em>}</div>
          <div><span>Service views</span><strong>1,204</strong></div>
        </div>
        <div className="hw-desk-tbl hw-card">
          <div className="hw-oprog-h"><b>Active orders</b><span className="hw-sm">See all</span></div>
          <div className="hw-desk-th"><span>Buyer</span><span>Work</span><span>Total</span><span>Status</span></div>
          {rows.map((r) => (
            <div key={r.who} className={`hw-desk-tr${r.fresh ? ' is-new' : ''}${r.fresh && f('clk') ? ' is-hit' : ''}`} data-cur={r.fresh ? 'row' : undefined} data-cx=".35">
              <span><Av>{r.av}</Av>{r.who}</span>
              <span>{r.work}</span>
              <span className="hw-desk-num">{r.total}</span>
              <span><i className={`hw-desk-st ${r.tone}`}>{r.status}</i></span>
            </div>
          ))}
        </div>
      </div>
      <div className={`hw-toast${arrived && !f('clk') ? ' is-on' : ''}`}>
        <Av ink>TB</Av>
        <span><b>New order request</b><span className="hw-sm">Theo B. · Standard · {PRICE_TXT}</span></span>
      </div>
    </div>
  );
}

// Payouts: the release, the month's earnings ticking up, and the review.
function PayoutScene({ f, at }) {
  return (
    <div className="hw-po">
      <div className={`hw-rel hw-po-top${f('show') ? ' hw-in' : ''}`}>
        <svg className="hw-ck" viewBox="0 0 74 74" fill="none" stroke="#1F7A4D" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle className="c" cx="37" cy="37" r="30" />
          <path className="t" d="M25 38l9 9 16-18" />
        </svg>
        <h3 className={h(f('h'))}>{PAYOUT_TXT} released to you</h3>
        <p className={h(f('p'))}>Theo approved the work. That&rsquo;s {PRICE_TXT} less the {SELLER_FEE_PCT}% fee.</p>
      </div>
      <div className="hw-po-tiles">
        <div className={h(f('e'), 'hw-card')}>
          <span className="hw-sm">Earned in October</span>
          <strong><CountUp from={340} to={340 + sellerPayout(PRICE)} at={at('e')} /></strong>
          <em>+{PAYOUT_TXT} today</em>
        </div>
        <div className={h(f('r'), 'hw-card')}>
          <span className="hw-sm">Theo B. left a review</span>
          <div className="hw-stars">
            {[1, 2, 3, 4, 5].map((n) => (
              <svg key={n} viewBox="0 0 24 24" strokeLinejoin="round" className={f(`s${n}`) ? 'on' : ''} aria-hidden="true">
                <path d="M12 3.2l2.6 5.5 6 .8-4.4 4.2 1.1 6-5.3-2.9-5.3 2.9 1.1-6L3.4 9.5l6-.8z" />
              </svg>
            ))}
          </div>
          <em>&ldquo;Exactly what our club needed.&rdquo;</em>
        </div>
      </div>
    </div>
  );
}

// Seller: upload the files on the order page and deliver.
function UploadScene({ f }) {
  return (
    <div className="hw-ord">
      <OrderHead seller status={f('dlv') ? 'Delivered' : 'In progress'} tone={f('dlv') ? AMBER : ''} />
      {f('dlv')
        ? <Callout icon={<ClockIcon />} lead="Theo reviews your delivery" rest=" Their payment stays held until they approve." />
        : (
          <Callout mine icon={<FileIcon size={15} />} lead="Deliver the work" rest=" Due in 5 days.">
            <span className={`hw-btn hw-onink${f('un') ? '' : ' hw-off'}${f('clk') ? ' hw-hit' : ''}`} data-cur="dv">Deliver work</span>
          </Callout>
        )}
      <div className="hw-dz">
        <div className={h(f('fi'), 'hw-file')}>
          <span className="hw-fi"><FileIcon /></span>
          <div className="hw-bx">
            <b>robotics-club-brand-kit.zip</b>
            <div className={`hw-track${f('prog') ? ' go' : ''}`}><i /></div>
          </div>
          <span className="hw-pc">{f('un') ? '100%' : f('prog') ? '…' : '0%'}</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Post a job: the posting, the comparison sheet, the offer, the contract ─ */
// A custom project, not a packaged service (2026-10-19): a small company
// hires a student to build its launch site. The comparison sheet and the
// offer are pages/JobPostings/ViewApplicants.jsx; the contract page is
// pages/Contracts/ContractDetail.jsx, the same "Clean data" look as the order
// page, so it reuses the order pieces above.
const JOB = {
  title: 'Build the launch website for our e-bike startup',
  company: 'Ridgeline Bikes',
  signer: 'Dana Whitfield',
  freelancer: 'Maya R.',
  freelancerSig: 'Maya Reyes',
  price: 1150, // Maya's quote, the one the offer is made at
};
const JOB_PRICE_TXT = formatUSD(JOB.price, 2);
const JOB_PAY_TXT = formatUSD(buyerTotal(JOB.price), 2);

function PostJobScene({ f, at }) {
  return (
    <>
      <div className="hw-jobh">
        <b>Post a job</b>
        {f('clk') ? <span className="hw-chip ok"><Check />Posted</span> : <span className="hw-chip grey">Draft</span>}
      </div>
      <div className="hw-field">
        <label>Job title</label>
        <div className="hw-fbox">
          <Typed text={JOB.title} from={at('t')} cps={50} />
          {f('t') && !f('bt') && <i className="hw-caret" />}
        </div>
      </div>
      <div className="hw-frow">
        <div className="hw-field">
          <label>Budget</label>
          <div className={h(f('bt'), 'hw-jseg')}>
            <span className="on">Fixed price</span><span>Hourly</span><span>Negotiable</span>
          </div>
        </div>
        <div className="hw-field hw-fnarrow">
          <label>Amount</label>
          <div className="hw-fbox"><Typed text="$1,200" from={at('b')} cps={10} /></div>
        </div>
      </div>
      <div className="hw-frow">
        <div className="hw-field">
          <label>Category</label>
          <div className="hw-fbox"><div className={h(f('d'))}>Programming &amp; Tech</div></div>
        </div>
        <div className="hw-field">
          <label>Who can apply</label>
          <div className="hw-fbox"><div className={h(f('v'))}>Verified students and alumni</div></div>
        </div>
      </div>
      <div className="hw-foot">
        <span>Only verified students and alumni can apply.</span>
        <span className={`hw-btn hw-brass${f('clk') ? ' hw-hit' : ''}`} data-cur="post">Post job</span>
      </div>
    </>
  );
}

// Applicants are columns, criteria are rows, and the strongest cell in a row
// gets a small ink tag. The real sheet has more rows (pitch, jobs done,
// checks, education...); three is what reads at this size.
const APPLICANTS = [
  { av: 'MR', name: 'Maya R.', school: 'OSU', quote: '$1,150', fit: 'Under your budget', days: '14 days', rating: '4.9', jobs: 12 },
  { av: 'JK', name: 'Jordan K.', school: 'PSU', quote: '$1,200', fit: 'On your budget', days: '21 days', rating: '5.0', jobs: 6 },
  { av: 'PS', name: 'Priya S.', school: 'UO', quote: '$1,250', fit: 'Over by $50', days: '10 days', rating: '4.8', jobs: 9 },
];
const COMPARE_ROWS = [
  { label: 'Their quote', best: 0, tag: 'Lowest', cell: (a) => <><b className="hw-cmp-big">{a.quote}</b><span className="hw-sm">{a.fit}</span></> },
  { label: 'Delivers in', best: 2, tag: 'Soonest', cell: (a) => <b>{a.days}</b> },
  { label: 'Rating', best: 1, tag: 'Highest', cell: (a) => <><b>★ {a.rating}</b><span className="hw-sm">{a.jobs} jobs done</span></> },
];
function CompareScene({ f }) {
  return (
    <>
      <div className="hw-cmp-h">
        <b>Compare your 3 applicants</b>
        <span className="hw-sm">{JOB.title} · $1,200 fixed</span>
      </div>
      <div className="hw-tabs">
        <span className="on">To decide <i>3</i></span><span>Offer sent</span><span>Hired</span><span>Declined</span>
      </div>
      <div className={h(f('c'), 'hw-cmp')}>
        <div className="hw-cmp-l" />
        {APPLICANTS.map((a, i) => (
          <div key={a.name} className={`hw-cmp-hd${i === 0 && f('pick') ? ' is-pick' : ''}`}>
            <Av>{a.av}</Av>
            <span><b>{a.name}</b><Verified school={a.school} /></span>
          </div>
        ))}
        {COMPARE_ROWS.map((r, ri) => (
          <React.Fragment key={r.label}>
            <div className={h(f(`r${ri + 1}`), 'hw-cmp-l')}>{r.label}</div>
            {APPLICANTS.map((a, i) => (
              <div key={a.name} className={h(f(`r${ri + 1}`), `hw-cmp-c${i === 0 && f('pick') ? ' is-pick' : ''}`)}>
                {r.cell(a)}
                {r.best === i && <span className={h(f('tags'), 'hw-tag')}>{r.tag}</span>}
              </div>
            ))}
          </React.Fragment>
        ))}
        <div className="hw-cmp-l hw-cmp-ft" />
        {APPLICANTS.map((a, i) => (
          <div key={a.name} className={`hw-cmp-c hw-cmp-ft${i === 0 && f('pick') ? ' is-pick' : ''}`}>
            <span className={`hw-btn hw-brass${i === 0 && f('clk') ? ' hw-hit' : ''}`} data-cur={i === 0 ? 'offer' : undefined}>Make an offer</span>
          </div>
        ))}
      </div>
    </>
  );
}

// The offer composer: it opens under the sheet on the real page.
const SCOPE = 'A five-page launch site with a pre-order flow, built mobile first and live by Nov 30.';
function OfferScene({ f, at }) {
  const sent = f('sent');
  return (
    <div className="hw-offer hw-card">
      <div className="hw-offer-h">
        <Av>MR</Av>
        <span><b>Offer to {JOB.freelancer}</b><span className="hw-sm">Their quote {JOB_PRICE_TXT} · fixed price</span></span>
      </div>
      <span className="hw-sh-lbl">What they are agreeing to do</span>
      <div className="hw-sh-ta hw-offer-ta">
        <Typed text={SCOPE} from={at('scope')} cps={45} />
        {f('scope') && !f('dt') && <i className="hw-caret" />}
      </div>
      <div className="hw-frow">
        <div className="hw-field">
          <label>Start date</label>
          <div className="hw-fbox"><div className={h(f('dt'))}>Oct 27, 2026</div></div>
        </div>
        <div className="hw-field">
          <label>End date (optional)</label>
          <div className="hw-fbox"><div className={h(f('dt'))}>Nov 30, 2026</div></div>
        </div>
      </div>
      <span className="hw-sh-lbl">Sign with your full name</span>
      <div className={`hw-sig${f('sig') ? ' is-signed' : ''}`}><span>{JOB.signer}</span></div>
      <div className="hw-offer-f">
        <span className="hw-sm">Nothing is charged until you fund the contract.</span>
        <span className={`hw-btn hw-brass${f('clk') ? ' hw-hit' : ''}`} data-cur="send">
          {sent ? <>Offer sent <Check size={12} /></> : `Send offer · ${JOB_PRICE_TXT}`}
        </span>
      </div>
    </div>
  );
}

const contractRows = ({ signed, funded, delivered, done }) => [
  { k: 'offer', label: 'Offer sent', done: true },
  { k: 'sign', label: 'Offer accepted and signed', done: signed },
  { k: 'fund', label: 'Escrow funded', done: funded, mine: signed && !funded },
  { k: 'dl', label: 'Work delivered', done: delivered },
  { k: 'ok', label: 'Approved and paid out', done, mine: delivered && !done },
];

// Maya accepts and signs; the client funds the contract.
function ContractFundScene({ f }) {
  const signing = f('cs');
  const signed = f('sg');
  const open = f('open');
  const paid = f('paid');
  return (
    <div className="hw-ord">
      <OrderHead contract status={paid ? 'In progress' : signed ? 'Awaiting funding' : 'Offer sent'} tone={paid ? '' : AMBER} />
      {!signing && <Callout icon={<PenIcon />} lead="Maya reviews your offer" rest=" She accepts and signs it, or asks for changes." />}
      {signing && !open && (
        <Callout
          icon={<PenIcon />}
          lead={signed ? 'Maya accepted and signed your offer' : 'Maya is signing'}
          rest={signed ? ' The contract is ready to fund.' : ' The scope, dates and price you offered.'}
        >
          <div className={`hw-sig hw-sig-ink${f('csw') ? ' is-signed' : ''}`}><span>{JOB.freelancerSig}</span></div>
        </Callout>
      )}
      {open && !paid && (
        <Callout mine icon={<LockIcon size={15} />} lead="Fund this contract" rest=" so Maya can get started.">
          <span className={`hw-btn hw-onink${f('clk') ? ' hw-hit' : ''}`} data-cur="fund">Fund contract</span>
        </Callout>
      )}
      {paid && <Callout icon={<ClockIcon />} lead="Funded. Maya is working on it" rest=" Held by Stripe until you approve the work." />}
      <div className="hw-ogrid">
        <Progress title="Contract progress" rows={contractRows({ signed, funded: paid })} />
        <PayBox held={paid} amount={JOB_PAY_TXT} />
      </div>
    </div>
  );
}

// The finished site, as screens: the homepage, the phone view, the pre-order
// flow. One brand (deep green, lime) runs through all three.
const RG = '#0E2A22';
const LIME = '#C6F24E';
const Bike = ({ x, y, s = 1, c }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`} fill="none" stroke={c} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle r="9" /><circle cx="32" r="9" />
    <path d="M0 0L12-15H25L32 0M12-15L16 0H0M16 0L25-15M25-15L27-21H32M9-18H15" />
    <rect x="13" y="-12.5" width="9" height="3.2" rx="1.2" fill={c} stroke="none" transform="rotate(-50 17 -11)" />
  </g>
);
Bike.propTypes = { x: PropTypes.number, y: PropTypes.number, s: PropTypes.number, c: PropTypes.string };
const SITE_FILES = [
  {
    label: 'Homepage',
    art: (
      <svg viewBox="0 0 170 86" aria-hidden="true" style={ART_FONT}>
        <rect width="170" height="86" fill="#EDEFEA" />
        <rect x="8" y="7" width="154" height="79" rx="5" fill={RG} />
        <rect x="8" y="7" width="154" height="9" rx="5" fill="#E4E7E1" />
        <circle cx="15" cy="11.5" r="1.6" fill="#B9BFB6" /><circle cx="20" cy="11.5" r="1.6" fill="#B9BFB6" /><circle cx="25" cy="11.5" r="1.6" fill="#B9BFB6" />
        <text x="17" y="28" fill={LIME} fontSize="5" fontWeight="800" letterSpacing="1">RIDGELINE</text>
        <text x="16" y="45" fill="#fff" fontSize="13" fontWeight="800" letterSpacing="-.5">Ride further.</text>
        <rect x="17" y="51" width="56" height="3" rx="1.5" fill="#fff" opacity=".35" />
        <rect x="17" y="62" width="34" height="11" rx="5.5" fill={LIME} />
        <text x="34" y="69.6" fill={RG} fontSize="5" fontWeight="800" textAnchor="middle">Pre-order</text>
        <circle cx="128" cy="54" r="24" fill="#16392E" />
        <Bike x={111} y={64} s={1} c={LIME} />
      </svg>
    ),
  },
  {
    label: 'Phone view',
    art: (
      <svg viewBox="0 0 170 86" aria-hidden="true" style={ART_FONT}>
        <rect width="170" height="86" fill="#DCE8D6" />
        <rect x="64" y="6" width="42" height="80" rx="7" fill="#111" />
        <rect x="67" y="9" width="36" height="77" rx="5" fill={RG} />
        <text x="72" y="22" fill={LIME} fontSize="3.6" fontWeight="800" letterSpacing=".6">RIDGELINE</text>
        <text x="72" y="33" fill="#fff" fontSize="7" fontWeight="800" letterSpacing="-.3">Ride</text>
        <text x="72" y="41" fill="#fff" fontSize="7" fontWeight="800" letterSpacing="-.3">further.</text>
        <Bike x={73} y={62} s={0.62} c={LIME} />
        <rect x="72" y="72" width="26" height="8" rx="4" fill={LIME} />
      </svg>
    ),
  },
  {
    label: 'Pre-order flow',
    art: (
      <svg viewBox="0 0 170 86" aria-hidden="true" style={ART_FONT}>
        <rect width="170" height="86" fill="#F5F6F2" />
        <rect x="10" y="9" width="64" height="68" rx="6" fill="#E3EADF" />
        <Bike x={24} y={52} s={0.95} c={RG} />
        <text x="84" y="21" fill={RG} fontSize="8" fontWeight="800">Ridgeline One</text>
        <text x="84" y="32" fill="#5E6370" fontSize="5.5" fontWeight="600">80 km range · 25 km/h</text>
        <circle cx="88" cy="42" r="3.6" fill={RG} /><circle cx="98" cy="42" r="3.6" fill="#C9542F" /><circle cx="108" cy="42" r="3.6" fill="#D9D4C7" />
        <circle cx="88" cy="42" r="5.6" fill="none" stroke={RG} strokeWidth=".9" />
        <text x="84" y="58" fill={RG} fontSize="10" fontWeight="800">$1,890</text>
        <rect x="84" y="64" width="72" height="12" rx="6" fill={LIME} />
        <text x="120" y="72" fill={RG} fontSize="5" fontWeight="800" textAnchor="middle">Reserve · $99 deposit</text>
      </svg>
    ),
  },
];

// Maya marks it complete with the files; the client approves and pays out.
function ContractDoneScene({ f }) {
  const done = f('done');
  return (
    <div className="hw-ord">
      <OrderHead contract status={done ? 'Completed' : 'Delivered'} tone={done ? 'ok' : AMBER} />
      <DeliveryCard f={f} files={SITE_FILES} zip="ridgeline-launch-site.zip" />
      <div className={h(f('acts'))}>
        {done
          ? <Callout done icon={<Check size={14} />} lead="Completed" rest={` ${JOB_PRICE_TXT} released to Maya.`} />
          : (
            <Callout mine icon={<Check size={14} />} lead="Maya marked this complete" rest=" Auto-completes in 7 days.">
              <span className="hw-btn hw-ghost-ink">Request a revision</span>
              <span className={`hw-btn hw-onink${f('clk') ? ' hw-hit' : ''}`} data-cur="appr">Approve &amp; release</span>
            </Callout>
          )}
      </div>
    </div>
  );
}

/* ─── cue tables ───────────────────────────────────────────────────────────── */
// [name, atMs, meta]. Keep sorted by time. A scene ends `settle` ms after its
// last cue (default 900: the last fade or click finishes and the result can
// be read). The section then holds that finished frame for 2s before the
// next step.
//
// Pacing rules (2026-10-19, re-timed after "some areas are too fast"):
// - a cursor move (`mv*`) gets 950ms before its click: .hw-cur travels 0.85s;
// - anything new to read gets time before the cursor heads off: about 1.2s
//   for a short line, 1.8-2.4s for a card or a sentence that matters;
// - typing gets length / cps to finish before the next cue assumes it has.
const SCENES = {
  search: {
    label: 'Browse services',
    Comp: SearchScene,
    cues: [
      ['q', 350, { pill: ['Searching', 'work'] }],
      // 24 characters at 40/s: typed by about 950.
      ['c1', 1150], ['c2', 1300], ['c3', 1450],
      ['done', 1650, { pill: ['3 services', 'ok'] }],
      ['mv', 3100, { cur: 'card0' }],
      ['clk', 4050, { press: true }],
    ],
    settle: 700,
  },
  request: {
    label: 'Order request',
    Comp: RequestScene,
    cues: [
      ['start', 150, { pill: ['Celest’s service', 'work'] }],
      ['mv1', 1400, { cur: 'rq' }],
      ['clk1', 2350, { press: true }],
      ['open', 2400, { pill: ['Step 1 of 3: brief', 'work'] }],
      // 84 characters at 45/s: typed by about 5300.
      ['brief', 3400],
      ['mv2', 5900, { cur: 'nx' }],
      ['clk2', 6850, { press: true }],
      ['pkg', 6900, { pill: ['Step 2 of 3: package', 'work'] }],
      ['clk3', 9000, { press: true }],
      ['sign', 9050, { pill: ['Step 3 of 3: sign', 'work'] }],
      // The signature writes itself in over 1s (.hw-sig span).
      ['sig', 10500],
      ['clk4', 12400, { press: true }],
      ['sent', 12450, { pill: ['Request sent · $0.00 charged', 'ok'] }],
    ],
    settle: 1200,
  },
  orderPay: {
    label: 'Your order',
    Comp: OrderPayScene,
    cues: [
      ['start', 150, { pill: ['Waiting on Celest', 'work'] }],
      // Read the waiting line, watch Celest sign (1s write-in), read that
      // it's signed and payment is open, and only then the pay box.
      // Slowed again 2026-10-19 ("slow it down more"); the write-in here
      // takes 1.6s (.hw-sig-ink).
      ['cs', 5000, { pill: ['Celest is signing', 'work'] }],
      ['csw', 5050],
      ['sg', 7200, { pill: ['Celest signed', 'ok'] }],
      ['open', 10500, { pill: ['Your move: pay', 'work'] }],
      ['mv', 13000, { cur: 'pay' }],
      ['clk', 14000, { press: true }],
      ['paid', 14050, { pill: ['Payment held in escrow', 'ok'] }],
    ],
    settle: 2500,
  },
  deliver: {
    label: 'Your order',
    Comp: DeliverScene,
    cues: [
      ['start', 100, { pill: ['Delivered', 'ok'] }],
      ['m1', 600], ['m2', 850], ['m3', 1100],
      ['fm', 1600],
      ['acts', 2600],
      ['mv', 4600, { cur: 'appr' }],
      ['clk', 5550, { press: true }],
    ],
    settle: 700,
  },
  release: {
    label: 'Your order',
    Comp: ReleaseScene,
    cues: [
      ['show', 150, { pill: ['Released', 'ok'] }],
      ['h', 650], ['p', 1000], ['stars', 1500],
      ['s1', 1650], ['s2', 1800], ['s3', 1950], ['s4', 2100], ['s5', 2250],
      ['rv', 2700],
    ],
    settle: 1300,
  },
  payout: {
    label: 'Payouts',
    Comp: PayoutScene,
    cues: [
      ['show', 150, { pill: ['Released', 'ok'] }],
      ['h', 650], ['p', 1000],
      // Earnings count up over 1.1s.
      ['e', 1800, { pill: ['Earnings updated', 'ok'] }],
      ['r', 3400],
      ['s1', 3600], ['s2', 3750], ['s3', 3900], ['s4', 4050], ['s5', 4200],
    ],
    settle: 2200,
  },
  listing: {
    label: 'Your service',
    Comp: ListingScene,
    cues: [
      ['start', 100, { pill: ['Drafting', 'work'] }],
      // 35 characters at 45/s: typed by about 1300.
      ['t', 500],
      ['k1', 1600], ['k2', 1800], ['k3', 2000],
      // Prices type at 12/s: "$160" is done 350ms after it starts.
      ['p1', 2300], ['p2', 2800], ['p3', 3300],
      ['g', 4100, { pill: ['Preview ready', 'work'] }],
      ['mv', 5600, { cur: 'pub' }],
      ['clk', 6550, { press: true }],
      ['live', 6600, { pill: ['Published · listing is free', 'ok'] }],
    ],
    settle: 1500,
  },
  requests: {
    label: 'Selling dashboard',
    Comp: RequestsScene,
    cues: [
      ['start', 100, { pill: ['Overview', 'work'] }],
      ['new', 2200, { pill: ['New order request', 'work'] }],
      ['mv', 5200, { cur: 'row' }],
      ['clk', 6150, { press: true }],
      ['open', 6200, { pill: ['Opening the order', 'work'] }],
    ],
    settle: 800,
  },
  sellerOrder: {
    label: 'Order from Theo B.',
    Comp: SellerOrderScene,
    cues: [
      ['start', 150, { pill: ['Theo signed a request', 'work'] }],
      ['mv', 2600, { cur: 'sign' }],
      ['clk', 3550, { press: true }],
      // Mounted unsigned on the click, then signed: the signature writes
      // itself in over 1s.
      ['sgn', 3600],
      ['signed', 5200, { pill: ['You signed', 'ok'] }],
      ['paid', 7600, { pill: ['Theo paid into escrow', 'ok'] }],
    ],
    settle: 2000,
  },
  upload: {
    label: 'Your order',
    Comp: UploadScene,
    cues: [
      ['start', 100, { pill: ['In progress', 'work'] }],
      ['fi', 900], ['prog', 1300], ['un', 2600],
      ['mv', 3300, { cur: 'dv' }],
      ['clk', 4250, { press: true }],
      ['dlv', 4300, { pill: ['Delivered', 'ok'] }],
    ],
    settle: 1800,
  },
  postJob: {
    label: 'Post a job',
    Comp: PostJobScene,
    cues: [
      ['start', 100, { pill: ['Drafting', 'work'] }],
      // 40 characters at 50/s: typed by about 1300.
      ['t', 500], ['bt', 1900], ['b', 2400], ['d', 3000], ['v', 3500],
      ['mv', 5000, { cur: 'post' }],
      ['clk', 5950, { press: true }],
      ['posted', 6000, { pill: ['Job posted', 'ok'] }],
    ],
    settle: 1200,
  },
  compare: {
    label: 'Applicants',
    Comp: CompareScene,
    cues: [
      // The heading and tabs come first, alone, so it's clear what this page
      // is before the sheet fills in.
      ['start', 100, { pill: ['3 applied', 'work'] }],
      ['c', 1800],
      ['r1', 2500], ['r2', 2900], ['r3', 3300],
      ['tags', 4300, { pill: ['Strongest in each row marked', 'ok'] }],
      ['mv', 7200, { cur: 'offer' }],
      ['clk', 8150, { press: true }],
      ['pick', 8200, { pill: ['Making an offer to Maya', 'work'] }],
    ],
    settle: 1300,
  },
  offer: {
    label: 'Your offer',
    Comp: OfferScene,
    cues: [
      ['start', 100, { pill: ['Offer to Maya', 'work'] }],
      // 84 characters at 45/s: typed by about 2600.
      ['scope', 700],
      ['dt', 3300],
      // The signature writes itself in over 1s.
      ['sig', 4300],
      ['mv', 6000, { cur: 'send' }],
      ['clk', 6950, { press: true }],
      ['sent', 7000, { pill: ['Offer sent · $0.00 charged', 'ok'] }],
    ],
    settle: 1500,
  },
  contractFund: {
    label: 'Your contract',
    Comp: ContractFundScene,
    // Same pacing as orderPay: read, watch the signature, read, then act.
    cues: [
      ['start', 150, { pill: ['Waiting on Maya', 'work'] }],
      ['cs', 5000, { pill: ['Maya is signing', 'work'] }],
      ['csw', 5050],
      ['sg', 7200, { pill: ['Maya accepted', 'ok'] }],
      ['open', 10500, { pill: ['Your move: fund it', 'work'] }],
      ['mv', 13000, { cur: 'fund' }],
      ['clk', 14000, { press: true }],
      ['paid', 14050, { pill: ['Contract funded', 'ok'] }],
    ],
    settle: 2500,
  },
  contractDone: {
    label: 'Your contract',
    Comp: ContractDoneScene,
    cues: [
      ['start', 100, { pill: ['Maya delivered', 'ok'] }],
      ['m1', 600], ['m2', 850], ['m3', 1100],
      ['fm', 1600],
      ['acts', 2600],
      ['mv', 4800, { cur: 'appr' }],
      ['clk', 5750, { press: true }],
      ['done', 5800, { pill: ['Payment released', 'ok'] }],
    ],
    settle: 2500,
  },
};

const endOf = (scene) => scene.cues[scene.cues.length - 1][1] + (scene.settle ?? 900);

// How long a scene plays, start to finished frame.
export const sceneMs = (id) => endOf(SCENES[id]);

// Clock resolution for SceneScreen's own clock and the section's.
export const TICK_MS = 50;


/* ─── the screen: shows one scene at a point in time ───────────────────────── */

const CURSOR = (
  <svg viewBox="0 0 22 22" aria-hidden="true">
    <path d="M3 2l14 7.6-6.2 1.7L8.4 18z" fill="#12161C" stroke="#fff" strokeWidth="1.5" strokeLinejoin="round" />
  </svg>
);

// `t` is how far into the scene to show, in ms, from the section's clock, so
// the section can pause it. Without `t` (the About page) the screen runs its
// own clock once through.
export default function SceneScreen({ id, instant, t }) {
  const scene = SCENES[id];
  const { cues, Comp } = scene;
  const end = endOf(scene);
  const driven = t != null;
  const [own, setOwn] = useState(0);
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 560, y: 372 });
  const bodyRef = useRef(null);
  const stageRef = useRef(null);

  useEffect(() => {
    if (driven || instant) return undefined;
    const t0 = performance.now();
    const iv = setInterval(() => {
      const e = performance.now() - t0;
      setOwn(Math.min(e, end));
      if (e >= end) clearInterval(iv);
    }, TICK_MS);
    return () => clearInterval(iv);
  }, [driven, instant, end]);

  const time = instant ? Infinity : driven ? t : own;
  const n = cues.filter((c) => c[1] <= time).length;

  // The stage is authored at 600px wide; scale it to whatever the screen is.
  useLayoutEffect(() => {
    const el = bodyRef.current;
    const ro = new ResizeObserver(([e]) => setScale(e.contentRect.width / 600));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const fired = cues.slice(0, n);
  const pill = [...fired].reverse().find((c) => c[2] && c[2].pill);
  const target = [...fired].reverse().find((c) => c[2] && c[2].cur);
  const pressing = n > 0 && cues[n - 1][2] && cues[n - 1][2].press;
  const curName = target && target[2].cur;

  // Cursor tracks the element tagged data-cur="name" inside the stage.
  useLayoutEffect(() => {
    if (!curName || !stageRef.current) return;
    const el = stageRef.current.querySelector(`[data-cur="${curName}"]`);
    if (!el) return;
    const sr = stageRef.current.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    const k = sr.width / 600;
    setPos({
      x: (r.left - sr.left + r.width * parseFloat(el.dataset.cx || 0.5)) / k,
      y: (r.top - sr.top + r.height * parseFloat(el.dataset.cy || 0.5)) / k,
    });
  }, [curName]);

  const f = (name) => {
    const i = cues.findIndex((c) => c[0] === name);
    return i >= 0 && i < n;
  };
  const at = (name) => {
    const c = cues.find((x) => x[0] === name);
    return c ? c[1] : null;
  };

  return (
    <div className="hw-screen">
      <div className="hw-screen-h">
        <span>{scene.label}</span>
        <span className={`hw-pill ${pill && pill[2].pill[1] ? pill[2].pill[1] : ''}`}>{pill ? pill[2].pill[0] : ''}</span>
      </div>
      <div className="hw-body" ref={bodyRef} aria-hidden="true">
        <div className="hw-stage" ref={stageRef} style={{ transform: `scale(${scale})` }}>
          <div className="hw-layer">
            <Instant.Provider value={!!instant}>
              <Clock.Provider value={time}>
                <Comp f={f} at={at} />
              </Clock.Provider>
            </Instant.Provider>
          </div>
          {pressing && <span key={n} className="hw-rip" style={{ left: pos.x, top: pos.y }} />}
          <i className="hw-cur" style={{ transform: `translate(${pos.x}px, ${pos.y}px)` }}>
            {React.cloneElement(CURSOR, { key: pressing ? `p${n}` : 'idle', className: pressing ? 'hw-press' : '' })}
          </i>
        </div>
      </div>
    </div>
  );
}

SceneScreen.propTypes = {
  id: PropTypes.oneOf(Object.keys(SCENES)).isRequired,
  instant: PropTypes.bool,
  t: PropTypes.number,
};
